"use client";

import {
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  ExternalLink,
  Maximize,
  Minimize,
  PictureInPicture2,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ArticleVideoDockContext } from "@/components/article-shell/ArticleShellContext";
import { useIsDesktop } from "@/hooks/useViewport";
import styles from "./YouTube.module.css";

type ThumbnailQuality = "default" | "medium" | "high" | "standard" | "maxres";

interface YouTubeProps {
  id: string;
  title?: string;
  caption?: string;
  showCaption?: boolean;
  start?: number | string;
  maxWidth?: number;
  thumbnailQuality?: ThumbnailQuality;
  className?: string;
  /** Keep one iframe mounted while switching between inline, mini and fullscreen. */
  enableViewingModes?: boolean;
}

const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{6,32}$/;

const THUMBNAIL_FILE: Record<ThumbnailQuality, string> = {
  default: "default",
  medium: "mqdefault",
  high: "hqdefault",
  standard: "sddefault",
  maxres: "maxresdefault",
};

function getSafeVideoId(id: string) {
  const trimmedId = id.trim();
  return YOUTUBE_ID_PATTERN.test(trimmedId) ? trimmedId : null;
}

function getStartSeconds(start: YouTubeProps["start"]) {
  if (start === undefined || start === null || start === "") return null;

  const parsed = typeof start === "number" ? start : Number(start);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;

  return Math.floor(parsed);
}

function getEmbedSrc(videoId: string, start: YouTubeProps["start"]) {
  const params = new URLSearchParams({
    autoplay: "1",
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
  });

  const startSeconds = getStartSeconds(start);
  if (startSeconds !== null) {
    params.set("start", String(startSeconds));
  }

  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}

function getThumbnailSrc(videoId: string, quality: ThumbnailQuality) {
  return `https://i.ytimg.com/vi/${videoId}/${THUMBNAIL_FILE[quality]}.jpg`;
}

/**
 * 페이지 크기 플레이어(expanded)일 때 패널 조상 경로 밖의 형제 요소를 모두 inert 로 만들고,
 * 이전 inert 값을 되돌리는 함수를 돌려준다. 컴포넌트 밖에 두어 React Compiler 가
 * ref 로부터 파생된 DOM 노드 변경으로 보지 않게 한다.
 */
function makeSiblingsInert(panel: HTMLElement): () => void {
  const siblings: Array<{ element: HTMLElement; inert: boolean }> = [];
  let branch: HTMLElement = panel;
  while (branch.parentElement) {
    for (const sibling of branch.parentElement.children) {
      if (sibling !== branch && sibling instanceof HTMLElement) {
        siblings.push({ element: sibling, inert: sibling.inert });
        sibling.inert = true;
      }
    }
    branch = branch.parentElement;
  }
  return () => {
    siblings.forEach(({ element, inert }) => {
      element.inert = inert;
    });
  };
}

export default function YouTube({
  id,
  title,
  caption,
  showCaption = true,
  start,
  maxWidth,
  thumbnailQuality = "high",
  className,
  enableViewingModes = false,
}: YouTubeProps) {
  const [activated, setActivated] = useState(false);
  const [manualMini, setManualMini] = useState(false);
  const [scrolledPast, setScrolledPast] = useState(false);
  const [dockHeight, setDockHeight] = useState(0);
  const [dockPosition, setDockPosition] = useState({ left: 0, top: 12, width: 0 });
  const [expanded, setExpanded] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [notice, setNotice] = useState("");
  const [placeholderHeight, setPlaceholderHeight] = useState<number>();
  const slotRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const fullscreenButtonRef = useRef<HTMLButtonElement>(null);
  const helpId = useId();
  const fullscreenPending = useRef(false);
  const fullscreenScrollPosition = useRef<{
    left: number;
    top: number;
  } | null>(null);
  const dockSlot = useContext(ArticleVideoDockContext);
  const isDesktop = useIsDesktop();
  const autoDock = enableViewingModes && isDesktop && dockSlot !== null;
  const docked = autoDock && scrolledPast;
  const mini = isDesktop && (autoDock ? docked : manualMini);

  // Observe the original space, not the fixed player, to avoid scroll oscillation.
  useEffect(() => {
    const slot = slotRef.current;
    if (!autoDock || !slot) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          fullscreenPending.current ||
          document.fullscreenElement === panelRef.current
        ) return;
        setScrolledPast(
          !entry.isIntersecting && entry.boundingClientRect.bottom <= 56,
        );
      },
      { rootMargin: "-56px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(slot);
    return () => observer.disconnect();
  }, [autoDock]);

  // Only a spacer enters the sidebar. Moving the iframe would restart playback.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!docked || !dockSlot || !panel || expanded || fullscreen) return;
    const measure = () => {
      const { left, top, width } = dockSlot.getBoundingClientRect();
      setDockPosition((previous) =>
        previous.left === left && previous.top === top && previous.width === width
          ? previous
          : { left, top, width },
      );
      setDockHeight(panel.getBoundingClientRect().height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(dockSlot);
    observer.observe(panel);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [docked, dockSlot, expanded, fullscreen]);

  useEffect(() => {
    if (!enableViewingModes) return;
    let restoreFrame = 0;
    const updateFullscreen = () => {
      const active = document.fullscreenElement === panelRef.current;
      setFullscreen(active);
      const position = fullscreenScrollPosition.current;
      if (!active && position) {
        fullscreenScrollPosition.current = null;
        // Safari may scroll the original iframe into view on leaving fullscreen.
        restoreFrame = requestAnimationFrame(() => {
          window.scrollTo({ ...position, behavior: "instant" });
        });
      }
    };
    document.addEventListener("fullscreenchange", updateFullscreen);
    return () => {
      cancelAnimationFrame(restoreFrame);
      document.removeEventListener("fullscreenchange", updateFullscreen);
    };
  }, [enableViewingModes]);

  // Reserve the measured inline space while the same panel is fixed.
  useEffect(() => {
    const panel = panelRef.current;
    if (!enableViewingModes || !panel || mini || expanded || fullscreen) return;
    const observer = new ResizeObserver(() => {
      setPlaceholderHeight(panel.getBoundingClientRect().height);
    });
    observer.observe(panel);
    return () => observer.disconnect();
  }, [enableViewingModes, mini, expanded, fullscreen, activated]);

  // iPhone and denied Fullscreen API requests use a page-sized player.
  useEffect(() => {
    const panel = panelRef.current;
    if (!expanded || !panel) return;
    const fullscreenButton = fullscreenButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    const restoreSiblings = makeSiblingsInert(panel);
    document.body.style.overflow = "hidden";
    fullscreenButton?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
      if (event.key !== "Tab") return;
      const controls = panel.querySelectorAll<HTMLElement>(
        "button:not(:disabled), a[href], iframe",
      );
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      restoreSiblings();
      document.removeEventListener("keydown", onKeyDown);
      fullscreenButton?.focus({ preventScroll: true });
    };
  }, [expanded]);

  // Moving a YouTube iframe into Document PiP resets its browsing context.
  // Change CSS only: no portal, reparenting, src changes or second player.
  function toggleMini() {
    if (mini) {
      setManualMini(false);
      setScrolledPast(false);
      slotRef.current?.scrollIntoView({ block: "center", behavior: "instant" });
      fullscreenButtonRef.current?.focus({ preventScroll: true });
    } else {
      setManualMini(true);
    }
  }

  async function toggleFullscreen() {
    const panel = panelRef.current;
    if (!panel || fullscreenPending.current) return;
    if (expanded) {
      setExpanded(false);
      return;
    }
    if (document.fullscreenElement === panel) {
      try {
        fullscreenPending.current = true;
        await document.exitFullscreen();
      } catch {
        setNotice(
          "전체화면을 닫으려면 Esc 또는 브라우저의 닫기 버튼을 눌러주세요.",
        );
      } finally {
        fullscreenPending.current = false;
      }
      return;
    }
    if (document.fullscreenEnabled && panel.requestFullscreen) {
      try {
        fullscreenPending.current = true;
        fullscreenScrollPosition.current = {
          left: window.scrollX,
          top: window.scrollY,
        };
        await panel.requestFullscreen();
        return;
      } catch {
        fullscreenScrollPosition.current = null;
        // Permission or browser restrictions: expand without moving the iframe.
      } finally {
        fullscreenPending.current = false;
      }
    }
    setExpanded(true);
  }

  const videoId = useMemo(() => getSafeVideoId(id), [id]);
  const accessibleTitle = title ?? caption ?? "YouTube 영상";
  const embedSrc = useMemo(
    () => (videoId ? getEmbedSrc(videoId, start) : null),
    [videoId, start],
  );
  const thumbnailSrc = useMemo(
    () => (videoId ? getThumbnailSrc(videoId, thumbnailQuality) : null),
    [videoId, thumbnailQuality],
  );

  return (
    <figure
      className={cn("my-8 text-center", className)}
      style={
        maxWidth
          ? {
              maxWidth: `${maxWidth}px`,
              marginLeft: "auto",
              marginRight: "auto",
            }
          : undefined
      }
    >
      {!videoId || !embedSrc || !thumbnailSrc ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          유효하지 않은 YouTube 영상 ID입니다. 영상 ID를 확인해주세요.
        </div>
      ) : (
        <div
          ref={slotRef}
          style={
            mini || expanded || fullscreen
              ? { height: placeholderHeight }
              : undefined
          }
        >
          <div
            ref={panelRef}
            className={cn(
              "overflow-hidden rounded-lg border border-border bg-muted shadow-lg dark:shadow-2xl dark:shadow-gray-800",
              enableViewingModes && styles.panel,
              mini && styles.mini,
              docked && styles.docked,
              expanded && styles.expanded,
            )}
            style={
              docked && dockPosition.width > 0
                ? ({
                    "--video-dock-left": `${dockPosition.left}px`,
                    "--video-dock-top": `${dockPosition.top}px`,
                    "--video-dock-width": `${dockPosition.width}px`,
                  } as React.CSSProperties)
                : undefined
            }
            role={expanded ? "dialog" : undefined}
            aria-modal={expanded ? true : undefined}
            aria-label={expanded ? accessibleTitle : undefined}
            data-view-mode={
              enableViewingModes
                ? fullscreen || expanded
                  ? "fullscreen"
                  : mini
                    ? "mini"
                    : "inline"
                : undefined
            }
          >
            <div
              className={cn(
                "relative aspect-video w-full bg-black",
                enableViewingModes && styles.video,
              )}
            >
              {activated ? (
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={embedSrc}
                  title={accessibleTitle}
                  loading="lazy"
                  onError={() =>
                    enableViewingModes &&
                    setNotice(
                      "영상을 불러오지 못했습니다. YouTube에서 보기를 이용해주세요.",
                    )
                  }
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  className="group relative h-full w-full touch-manipulation overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500"
                  onClick={() => setActivated(true)}
                  aria-label={`${accessibleTitle} 재생`}
                >
                  <span
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105 motion-reduce:transform-none motion-reduce:transition-none"
                    style={{ backgroundImage: `url(${thumbnailSrc})` }}
                    aria-hidden="true"
                  />
                  <span
                    className="absolute inset-0 bg-black/35 transition-colors group-hover:bg-black/25 motion-reduce:transition-none"
                    aria-hidden="true"
                  />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-red-600 shadow-lg transition-transform group-hover:scale-105 motion-reduce:transform-none motion-reduce:transition-none dark:bg-gray-950/95 dark:text-red-500 sm:h-20 sm:w-20">
                      <Play
                        className="ml-1 h-8 w-8 fill-current sm:h-10 sm:w-10"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      />
                    </span>
                  </span>
                  <span className="sr-only">동영상 플레이어를 불러옵니다.</span>
                </button>
              )}
            </div>
            {enableViewingModes && (
              <div className={styles.controls}>
                {isDesktop && !autoDock && (
                  <button
                    type="button"
                    className={styles.control}
                    disabled={!activated || fullscreen || expanded}
                    onClick={toggleMini}
                    aria-pressed={mini}
                    aria-describedby={helpId}
                  >
                    <PictureInPicture2 className="size-4" aria-hidden="true" />
                    {mini ? "원래 위치로" : "작은 창"}
                  </button>
                )}
                <button
                  ref={fullscreenButtonRef}
                  type="button"
                  className={styles.control}
                  disabled={!activated}
                  onClick={toggleFullscreen}
                >
                  {fullscreen || expanded ? (
                    <Minimize className="size-4" aria-hidden="true" />
                  ) : (
                    <Maximize className="size-4" aria-hidden="true" />
                  )}
                  {fullscreen || expanded ? "전체화면 종료" : "전체화면"}
                </button>
                <a
                  className={styles.control}
                  href={`https://www.youtube.com/watch?v=${videoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube에서 보기 (새 탭)"
                >
                  <ExternalLink className="size-4" aria-hidden="true" />
                  YouTube에서 보기
                </a>
              </div>
            )}
            {enableViewingModes && isDesktop && !autoDock && (
              <div id={helpId} className={styles.help}>
                작은 창은 이 페이지 안에서 표시돼요. 다른 탭에서는 영상이 보이지 않아요.
              </div>
            )}
            {expanded && (
              <div role="status" className={styles.help}>
                이 브라우저에서는 페이지 안에서 크게 표시합니다. 전체화면 종료로
                돌아갈 수 있어요.
              </div>
            )}
            {notice && (
              <div role="status" className={styles.help}>
                {notice}
              </div>
            )}
          </div>
        </div>
      )}

      {docked && dockSlot &&
        createPortal(
          <div aria-hidden data-video-dock-spacer style={{ height: dockHeight }} />,
          dockSlot,
        )}

      {showCaption && caption && (
        <figcaption className="mt-3 text-sm text-gray-600 dark:text-gray-400 italic leading-6">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
